import { useEffect, useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { Link, useNavigate } from "react-router-dom";

import { auth } from "../../services/firebaseAuth";
import { Alert, Button, Grid, Typography, useTheme } from "@mui/material";
import isEmail from "validator/lib/isEmail";
import { useAuthState } from "react-firebase-hooks/auth";
import CustomTextField from "../Base/CustomTextField";
import BackgroundWrapper from "../BackgroundWrapper";
import { getRedirectUrl } from "../../utils/redirectUtils";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleReset = async () => {
    try {
      await sendPasswordResetEmail(auth, email);
      setIsEmailSent(true);
      setErrorMsg("");
    } catch (error: any) {
      setErrorMsg("Erreur lors de l'envoi de l'e-mail. Vérifie l'adresse.");
    }
  };

  const [user, loading] = useAuthState(auth);

  useEffect(() => {
    if (loading) return;
    if (user) {
      // Rediriger vers l'URL sauvegardée ou la page d'accueil
      navigate(getRedirectUrl());
    }
  }, [user, loading, navigate]);

  const theme = useTheme();

  return (
    <BackgroundWrapper>
      <Grid
        container
        direction="column"
        rowSpacing={4}
        justifyContent="center"
        alignItems="center"
        sx={{
          maxWidth: "600px", // Largeur maximale
          width: "100%", // Prend toute la largeur disponible
          mx: "auto", // Centré horizontalement
        }}
      >
        <Grid item>
          <Typography
            variant="body1"
            sx={{
              width: "100%",
              textAlign: "center",
              bgcolor: theme.palette.secondary.main,
              color: theme.palette.text.primary,
              p: 2,
              borderRadius: 1,
            }}
          >
            Pour réinitialiser votre mot de passe, entrez votre adresse e-mail
            ci-dessous. Vous recevrez un e-mail avec les instructions pour
            réinitialiser votre mot de passe.
          </Typography>
        </Grid>
        <Grid item sx={{ width: "100%" }}>
          <CustomTextField
            fullWidth
            type="email"
            label="Email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            onClick={handleReset}
            disabled={!isEmail(email)}
            sx={{
              width: "100%",
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              mt: 2,
              backgroundColor: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
                transform: "scale(1.05)", // Agrandissement du bouton au survol
                transition: "transform 0.3s ease-in-out", // Transition douce
              },
            }}
          >
            Envoyer un e-mail de réinitialisation
          </Button>
          {isEmailSent && (
            <Grid item>
              <Alert sx={{ mt: 2 }} severity="success">
                Un e-mail a été envoyé pour réinitialiser votre mot de passe.
              </Alert>
            </Grid>
          )}
          {errorMsg && (
            <Grid item>
              <Alert severity="error">{errorMsg}</Alert>
            </Grid>
          )}
        </Grid>

        <Grid item container justifyContent="space-between" sx={{ mt: 2 }}>
          <Grid item>
            <Link
              to="/signup"
              style={{
                textDecoration: "none",
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            >
              Pas de compte ?
            </Link>
          </Grid>
          <Grid item>
            <Link
              to="/login"
              style={{
                textDecoration: "none",
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            >
              Déjà un compte ? Connectez-vous
            </Link>
          </Grid>
        </Grid>
      </Grid>
    </BackgroundWrapper>
  );
};

export default ResetPassword;
